/**
 * Ranked play for one session (Phase 7J): checks that the server ranks this
 * track at this build's revision, issues an attempt before the run, submits
 * the finished recording and reports the server's decision.
 *
 * Nothing here decides anything: the result shown is the server's replay of
 * the recording. Offline, or for a track the server does not rank, the game
 * simply plays unranked; local records and replays are unaffected.
 */
import { serializeRecording, type RunRecording } from '../replay/RecordingFormat';
import { RankedError, type RankedClient, type RankedDecision, type RankedGrant } from '../ranked/RankedClient';
import type { CatalogTrack } from './catalog';

export type RankedState =
  | { kind: 'checking' }
  | { kind: 'unavailable'; why: 'offline' | 'not-ranked' | 'version' }
  | { kind: 'available' }
  | { kind: 'arming' }
  | { kind: 'armed'; grant: RankedGrant }
  | { kind: 'submitting' }
  | { kind: 'decided'; decision: RankedDecision }
  | { kind: 'error'; code: string; message: string };

export class RankedFlow {
  private s: RankedState = { kind: 'checking' };
  /** The player chose ranked play for this session: every run is submitted, and retries re-arm. */
  private on = false;
  private disposed = false;

  constructor(
    private readonly client: RankedClient,
    private readonly track: CatalogTrack,
    private readonly changed: () => void,
  ) {}

  get state(): RankedState {
    return this.s;
  }

  get enabled(): boolean {
    return this.on;
  }

  /** Whether a ranked attempt can be started now (server reachable, track ranked at this revision). */
  get canArm(): boolean {
    return this.s.kind === 'available' || this.s.kind === 'decided' || this.s.kind === 'error';
  }

  get armed(): boolean {
    return this.s.kind === 'armed';
  }

  async probe(): Promise<void> {
    const tracks = await this.client.tracks();
    if (!tracks) return this.set({ kind: 'unavailable', why: 'offline' });
    const t = tracks.find((x) => x.key === this.track.key);
    if (!t) return this.set({ kind: 'unavailable', why: 'not-ranked' });
    // The server ranks another revision of this track or other movement rules than this build has.
    if (t.recordKey !== this.track.recordKey) return this.set({ kind: 'unavailable', why: 'version' });
    this.set({ kind: 'available' });
  }

  /** Issues an attempt (or reuses one a failed submission left open); true when the next run will be ranked. */
  async arm(): Promise<boolean> {
    if (this.armed) return true;
    if (!this.canArm) return false;
    this.on = true;
    const kept = this.unsent;
    this.unsent = null;
    if (kept && kept.expiresAt > Date.now() + 60_000) {
      this.set({ kind: 'armed', grant: kept });
      return true;
    }
    this.set({ kind: 'arming' });
    try {
      this.set({ kind: 'armed', grant: await this.client.attempt(this.track.key) });
      return true;
    } catch (err: unknown) {
      this.set(failure(err));
      return false;
    }
  }

  /** Submits a finished, eligible run on the open attempt. */
  async submit(rec: RunRecording): Promise<void> {
    if (this.s.kind !== 'armed') return;
    const grant = this.s.grant;
    this.set({ kind: 'submitting' });
    try {
      this.set({ kind: 'decided', decision: await this.client.submit(grant, await serializeRecording(rec)) });
    } catch (err: unknown) {
      const f = failure(err);
      // A busy server or a lost connection decided nothing: the attempt stays open for the next run.
      if (err instanceof RankedError && (err.status === 503 || err.status === 0)) this.unsent = grant;
      this.set(f);
    }
  }

  /** An attempt a submission could not reach the server on (it was not used). */
  private unsent: RankedGrant | null = null;

  dispose(): void {
    this.disposed = true;
  }

  private set(s: RankedState): void {
    this.s = s;
    if (!this.disposed) this.changed();
  }
}

function failure(err: unknown): Extract<RankedState, { kind: 'error' }> {
  if (err instanceof RankedError) return { kind: 'error', code: err.code, message: err.message };
  return { kind: 'error', code: 'client', message: err instanceof Error ? err.message : String(err) };
}
